/**
 * SimulationButton - Bouton pour lancer une simulation
 * À intégrer dans FormPollCreator
 */

import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

interface SimulationButtonProps {
  /** Callback au clic */
  onClick: () => void;

  /** Simulations restantes (aujourd'hui) */
  remainingSimulations: number;

  /** Désactivé */
  disabled?: boolean;

  /** Variant */
  variant?: "primary" | "secondary";

  /** Mode paywall : « Tester » est payant, le clic ouvre l'incitation à l'upgrade */
  isLocked?: boolean;
}

export function SimulationButton({
  onClick,
  remainingSimulations,
  disabled = false,
  variant = "secondary",
  isLocked = false,
}: SimulationButtonProps) {
  const { t } = useTranslation("forms");
  const isPrimary = variant === "primary";

  const isDisabled = isLocked ? false : disabled || remainingSimulations === 0;
  // Explique POURQUOI le bouton est grisé (sinon l'utilisateur reste sans
  // indice). Quota épuisé prime sur « formulaire non finalisé ».
  const disabledTitle = !isDisabled
    ? undefined
    : remainingSimulations === 0
      ? t("simulation.button.quotaReason")
      : t("simulation.button.disabledReason");

  return (
    <button
      onClick={onClick}
      // En mode paywall, le bouton reste cliquable (déclenche l'upgrade) ;
      // sinon il se désactive quand le quota du jour est épuisé.
      disabled={isDisabled}
      title={disabledTitle}
      data-testid="simulation-button"
      className={`
        flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors
        ${
          isPrimary
            ? "bg-primary text-primary-foreground hover:bg-primary/90"
            : "border border-border text-muted-foreground hover:bg-accent"
        }
        disabled:opacity-50 disabled:cursor-not-allowed
      `}
    >
      {isLocked && <Lock className="w-4 h-4" />}
      <span>{t("simulation.button.label")}</span>
      {!isLocked && remainingSimulations > 0 && (
        <span
          className={`
          text-xs px-2 py-0.5 rounded-full
          ${isPrimary ? "bg-primary-foreground/20" : "bg-primary text-primary-foreground"}
        `}
        >
          {remainingSimulations}
        </span>
      )}
    </button>
  );
}
